import {
  type UseMutationOptions,
  type UseQueryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { type AxiosError } from 'axios';

import {
  EmailService,
  InterviewShareService,
  type MailTemplateDto,
  type MailTemplateRequest,
  type SharedInterviewDtoPagedResponseDto,
  type TemplateTokenDto,
  type TemplateType,
} from '@/services/api';

const emailSettingsKeys = {
  getEmailSettingsTemplatesList: () =>
    ['getEmailSettingsTemplatesList'] as const,
  getEmailTemplateTokens: (params: GetEmailTemplateTokensQueryParams) =>
    ['getEmailTemplateTokens', params] as const,
  getEmailTemplate: (emailTemplateId: string) =>
    ['getEmailTemplate', emailTemplateId] as const,
};

const interviewSharesSettingsKeys = {
  getInterviewSharesList: (params?: GetInterviewSharesListQueryParams) =>
    ['getInterviewSharesList', params] as const,
};

export const useGetEmailSettingsTemplatesList = (
  options?: Partial<UseQueryOptions<Array<MailTemplateDto>, AxiosError>>
) => {
  return useQuery<Array<MailTemplateDto>, AxiosError>({
    queryKey: emailSettingsKeys.getEmailSettingsTemplatesList(),
    queryFn: () => EmailService.getEmailTemplate(),
    ...options,
  });
};

type GetEmailTemplateParams = {
  emailTemplateId: string;
};

export const useGetEmailTemplate = (
  params: GetEmailTemplateParams,
  options?: Partial<UseQueryOptions<MailTemplateDto, AxiosError>>
) => {
  return useQuery<MailTemplateDto, AxiosError>({
    queryKey: emailSettingsKeys.getEmailTemplate(params.emailTemplateId),
    queryFn: () =>
      EmailService.getAllEmailTemplate(
        { id: params.emailTemplateId },
        options?.axiosRequestConfig
      ),
    ...options,
  });
};

type GetEmailTemplateTokensQueryParams = {
  type: TemplateType;
};

export const useGetEmailTemplateTokens = (
  params: GetEmailTemplateTokensQueryParams,
  options?: Partial<UseQueryOptions<Array<TemplateTokenDto>, AxiosError>>
) => {
  return useQuery<Array<TemplateTokenDto>, AxiosError>({
    queryKey: emailSettingsKeys.getEmailTemplateTokens(params),
    queryFn: () =>
      EmailService.getEmailTemplateToken(params, options?.axiosRequestConfig),
    ...options,
  });
};

export const useCreateEmailTemplateMutation = (
  options?: UseMutationOptions<unknown, AxiosError, MailTemplateRequest>
) => {
  const queryClient = useQueryClient();

  return useMutation<unknown, AxiosError, MailTemplateRequest>({
    mutationFn: async (body) => {
      return EmailService.postEmailTemplate({
        body,
      });
    },
    ...options,
    async onSuccess(data, variables, context) {
      await queryClient.invalidateQueries({
        queryKey: emailSettingsKeys.getEmailSettingsTemplatesList(),
      });
      options?.onSuccess?.(data, variables, context);
    },
  });
};

export const useEditEmailTemplateMutation = (
  options?: UseMutationOptions<
    unknown,
    AxiosError,
    { templateId: string; body: MailTemplateRequest }
  >
) => {
  const queryClient = useQueryClient();

  return useMutation<
    unknown,
    AxiosError,
    { templateId: string; body: MailTemplateRequest }
  >({
    mutationFn: async ({ templateId, body }) => {
      return EmailService.putAllEmailTemplate({
        id: templateId,
        body,
      });
    },
    ...options,
    async onSuccess(data, variables, context) {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: emailSettingsKeys.getEmailSettingsTemplatesList(),
        }),
        queryClient.invalidateQueries({
          queryKey: emailSettingsKeys.getEmailTemplate(variables.templateId),
        }),
      ]);

      options?.onSuccess?.(data, variables, context);
    },
  });
};

export const useDeleteEmailTemplateMutation = (
  options?: Partial<
    UseMutationOptions<void, AxiosError, { templateId: string }>
  >
) => {
  const queryClient = useQueryClient();

  return useMutation<void, AxiosError, { templateId: string }>({
    mutationFn: ({ templateId }) =>
      EmailService.deleteEmailTemplateById({ id: templateId }),
    ...options,
    async onSuccess(data, variables, context) {
      await queryClient.invalidateQueries({
        queryKey: emailSettingsKeys.getEmailSettingsTemplatesList(),
      });
      options?.onSuccess?.(data, variables, context);
    },
  });
};

type GetInterviewSharesListQueryParams = {
  q?: string;
  remainingDayStart?: number;
  remainingDayEnd?: number;
  size?: number;
  page?: number;
};

export const useGetInterviewSharesList = (
  params: GetInterviewSharesListQueryParams,
  options?: Partial<
    UseQueryOptions<SharedInterviewDtoPagedResponseDto, AxiosError>
  >
) => {
  return useQuery<SharedInterviewDtoPagedResponseDto, AxiosError>({
    queryKey: interviewSharesSettingsKeys.getInterviewSharesList(params),
    queryFn: () => InterviewShareService.getApiInterviewShare(params),
    ...options,
  });
};
